"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.User = void 0;
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

const defaults_1 = __importDefault(require("lodash/defaults"));

const Member_1 = require("../member/Member");

const SURNAME_FIRST_LOCALES = ['ja', 'ko', 'zh-cn', 'zh-tw'];

class User extends Member_1.Member {
  /**
   * @class
   * @description Creates a new User for use. See Member class
   *    for additional params.
   *
   * @param {Object} [options] - User instance settings
   * @param {String} [options.accountStatus] User's account status
   * @param {String} [options.countryCode] - User's country code
   * @param {String} [options.email] - User's email
   * @param {Boolean} [options.externallyManaged] True if user was created from an external system
   * @param {String} [options.firstName] - User's first name
   * @param {String} [options.lastName] - User's last name
   * @param {Array} [options.licenseGroups] A User's license groups
   * @param {Array} [options.products] A User's products
   * @param {Array} [options.roles] A User's roles
   * @param {Array} [options.userGroups] A User's user groups
   * @param {String} [options.userName] - User's user name
   */
  constructor(options = {}) {
    super(options);
    this.accountStatus = options.accountStatus;
    this.countryCode = options.countryCode;
    this.email = options.email;
    this.externallyManaged = options.externallyManaged;
    this.firstName = options.firstName;
    this.lastName = options.lastName;
    this.licenseGroups = options.licenseGroups || [];
    this.products = options.products || [];
    this.roles = options.roles || [];
    this.userGroups = options.userGroups || [];
    this.userName = options.userName;
  }
  /**
   * @description Method to construct a User from a data Object containing
   *              the IMS profile information.
   *
   * @param {Object} imsData - Object containing IMS profile data
   * @returns {User} reference to User Object created
   */


  static imsProfileTransformer(imsData) {
    const {
      countryCode,
      email,
      first_name,
      userId,
      last_name,
      account_type
    } = imsData;
    return new User({
      countryCode,
      email,
      firstName: first_name,
      id: userId,
      lastName: last_name,
      type: account_type.toUpperCase()
    });
  }
  /**
   * @description Method to construct a User from a data Object containing
   *              user information.
   *
   * @param {Object} data - Object containing user data (e.g. - DTO)
   * @returns {User} reference to User Object created
   */


  static apiResponseTransformer(data) {
    const filteredData = {
      accountStatus: data.accountStatus,
      countryCode: data.countryCode,
      editable: data.editable,
      email: data.email,
      firstName: data.firstName,
      id: data.id,
      inTarget: data.inTarget,
      lastName: data.lastName,
      licenseGroups: data.licenseGroups,
      productRole: data.productRole,
      products: data.products,
      roles: data.roles,
      type: data.type,
      userGroups: data.userGroups,
      userName: data.userName
    };
    return new User(defaults_1.default({
      id: data.userId
    }, filteredData));
  }
  /** Public methods **/

  /**
   * @description Method to retrieve the full name of this user.
   *
   * @returns {String} Full name of user
   */


  getDisplayName() {
    let name;

    if (this.hasDisplayName()) {
      if (!this.firstName || this.firstName === 'UD') {
        name = this.lastName;
      } else if (!this.lastName || this.lastName === 'UD') {
        name = this.firstName;
      } else if (SURNAME_FIRST_LOCALES.includes(this.countryCode.toLowerCase())) {
        name = `${this.lastName} ${this.firstName}`;
      } else {
        name = `${this.firstName} ${this.lastName}`;
      }
    }

    return name;
  }
  /**
   * @description Method to retrieve the display name of this user, if the user doesn't have a name, then returns their email
   *
   * @returns {String} Display name or email of the user
   */


  getDisplayNameOrEmail() {
    return this.getDisplayName() || this.email;
  }
  /**
   * @description Method to determine if this User has a first or last name
   *
   * @returns {Boolean} true if the User has a first or last name, else false
   */


  hasDisplayName() {
    return !!(this.firstName && this.firstName !== 'UD' || this.lastName && this.lastName !== 'UD');
  }

}

exports.User = User;